import { DefaultUrlSerializer } from "@angular/router";

import { Locale } from "@hermes/locale";

import { isQQBrowerAgent } from "../browser/browser.helper";

/**
 * Cleanup url to match angular routes.
 *
 * @param path unsanitized url
 * @return path without first slash and no hash part
 */
export const pathCleaner = (path: string): string =>
    path
        // remove first slash
        .replace(/^\//, "")
        // remove char after hashbang
        .replace(/#(.*)$/, "")
        // remove back slash
        .replace(/\/$/, "");

/**
 * Obtain url substring that match a given regExp at a given index.
 *
 * @param url url string to search in
 * @param regExp expression to match a substring
 * @param index element index to output
 * @return matching element at specified index or empty string if no match
 */
export const getMatchAtIndex = (
    url: string | undefined,
    regExp: RegExp,
    index: number,
): string => {
    const result = url?.match(regExp);

    return result?.at(index) ?? "";
};

export function stripLocaleFromUrl(url: string, locale: Locale): string {
    return url.replace(locale.urlPrefix, "");
}

/**
 * @returns the product link to go to the product page from grid
 */
export const generateLocalizedHref = (url: string, locale: Locale): string => {
    // all product item url must end end with a '/'
    const end = url?.endsWith("/") ? "" : "/";
    // we want the full url with base href (/xx/yy/product/*) all the time.
    return `${locale.urlPrefix}${url}${end}`;
};

export const formatAssetUrl = (url: string, userAgent?: string): string => {
    url = url.replace(/\s/g, "");
    // Some browsers (namely QQBrowser) consider `,` as an srcset
    // separator. Encoding the comma prevents that
    if (isQQBrowerAgent(userAgent)) {
        url = url.replace(/,/g, "%2C");
    }
    return url;
};

/**
 * Compares two URLs and returns true if they are the same
 * while ignoring #fragment.
 *
 * Trailing slashes (/product/123/) are also ignored
 * to avoid false differences.
 */

export const sameUrlWithoutFragment = (
    firstUrl: string,
    secondUrl: string,
): boolean => {
    // oxlint-disable-next-line unicorn/no-null
    const emptyFragment = null;
    const defaultUrlSerializer = new DefaultUrlSerializer();
    const firstUrlParsed = defaultUrlSerializer.parse(firstUrl);
    const secondUrlParsed = defaultUrlSerializer.parse(secondUrl);
    firstUrlParsed.fragment = emptyFragment;
    secondUrlParsed.fragment = emptyFragment;
    const normalize = (x: string) => x.replace(/\/+$/, "") || "/";
    return (
        normalize(defaultUrlSerializer.serialize(firstUrlParsed)) ===
        normalize(defaultUrlSerializer.serialize(secondUrlParsed))
    );
};
